/**
 * Medication scheduling: dose times, countdowns and meal-relative dosing.
 *
 * Ported from patient_dashboard.js (`getNextDoseTime`, `getNextDoseCountdown`,
 * `generateDoseSchedule`, `getNextDoseTimeRemaining`, `isMealTimeMed`,
 * `getMealRemainingSeconds`, `getMealCountdown`, `getNextMealTimeStr`) and the
 * near-identical copies in admin_medicines.js and alarm.js.
 *
 * The legacy versions returned HTML strings. These return data; rendering is the
 * component's job. That separation is what makes the schedule testable.
 */

import { secondsSinceMidnight, formatCountdown, formatSecondsAs12h } from './dates';

const DAY_SECONDS = 86400;

export const MEAL_KEYS = ['breakfast', 'lunch', 'dinner'];

export const MEAL_LABELS = {
  breakfast: 'الإفطار',
  lunch: 'الغداء',
  dinner: 'العشاء',
};

/** Defaults used when a patient has not set their own meal times. */
export const DEFAULT_MEAL_TIMES = {
  breakfast: '08:00',
  lunch: '14:00',
  dinner: '20:00',
};

const toDaySeconds = (time) => {
  if (!time) return Number.NaN;
  const [h, m] = String(time).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return Number.NaN;
  return h * 3600 + m * 60;
};

/**
 * Every dose time for one day, as seconds since midnight.
 *
 * Doses are spaced evenly across 24 hours: `frequency` doses means one every
 * `24 / frequency` hours, starting at `firstDoseTime`. Times that run past
 * midnight wrap to the start of the same day, which is what the legacy schedule
 * badges displayed.
 *
 * @param {{firstDoseTime?: string, frequency?: number}} med
 * @returns {number[]} Ascending is *not* guaranteed — wrapped doses sort earlier.
 */
export function doseTimesInSeconds(med) {
  const first = toDaySeconds(med?.firstDoseTime);
  const frequency = Number(med?.frequency);
  if (Number.isNaN(first) || !Number.isFinite(frequency) || frequency <= 0) return [];

  const interval = DAY_SECONDS / frequency;
  return Array.from({ length: Math.floor(frequency) }, (_, i) => {
    const raw = first + i * interval;
    return raw >= DAY_SECONDS ? raw - DAY_SECONDS : raw;
  });
}

/**
 * The schedule as render-ready rows.
 *
 * @returns {Array<{seconds: number, time: string, isNext: boolean, isPast: boolean}>}
 */
export function buildDoseSchedule(med, now = new Date()) {
  const current = secondsSinceMidnight(now);
  const frequency = Number(med?.frequency) || 0;
  const interval = frequency > 0 ? DAY_SECONDS / frequency : DAY_SECONDS;

  return doseTimesInSeconds(med).map((seconds) => ({
    seconds,
    time: formatSecondsAs12h(seconds),
    isNext: seconds > current && seconds - current <= interval,
    isPast: seconds < current,
  }));
}

/**
 * Seconds until the next dose, rolling into tomorrow when today's are done.
 * Returns `null` when the medicine has no usable schedule.
 */
export function secondsUntilNextDose(med, now = new Date()) {
  const first = toDaySeconds(med?.firstDoseTime);
  const frequency = Number(med?.frequency);
  if (Number.isNaN(first) || !Number.isFinite(frequency) || frequency <= 0) return null;

  const current = secondsSinceMidnight(now);
  const interval = DAY_SECONDS / frequency;

  let next = first;
  // Walk forward one interval at a time until we pass "now".
  const guardLimit = Math.ceil(DAY_SECONDS / interval) + 2;
  for (let i = 0; next <= current && i < guardLimit; i += 1) next += interval;

  if (next >= DAY_SECONDS) next -= DAY_SECONDS;

  let remaining = next - current;
  if (remaining < 0) remaining += DAY_SECONDS;
  return Math.round(remaining);
}

/** Next dose as a wall-clock string, e.g. "3:05 PM". */
export function nextDoseTime(med, now = new Date()) {
  const remaining = secondsUntilNextDose(med, now);
  if (remaining === null) return '--:--';
  return formatSecondsAs12h(secondsSinceMidnight(now) + remaining);
}

/** Next dose as an "HH:MM:SS" countdown. */
export function nextDoseCountdown(med, now = new Date()) {
  const remaining = secondsUntilNextDose(med, now);
  if (remaining === null) return '⏳ حدد موعد الجرعة';
  return formatCountdown(remaining);
}

/** Arabic prose form: "الجرعة الجاية بعد ٣ ساعة و ١٠ دقيقة". */
export function nextDoseSummary(med, now = new Date()) {
  const remaining = secondsUntilNextDose(med, now);
  if (remaining === null) return 'حدد وقت أول جرعة';

  const totalMinutes = Math.round(remaining / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `الجرعة الجاية بعد ${minutes} دقيقة`;
  if (minutes === 0) return `الجرعة الجاية بعد ${hours} ساعة`;
  return `الجرعة الجاية بعد ${hours} ساعة و ${minutes} دقيقة`;
}

/* ==========================================================================
   Meal-relative dosing
   ========================================================================== */

/**
 * True when the doctor wrote the dose as "with meals" rather than a fixed
 * frequency — such medicines follow the patient's meal times instead.
 */
export function isMealTimeMedicine(med) {
  const text = `${med?.dose || ''} ${med?.dosage || ''}`.toLowerCase();
  return text.includes('وجبات') || text.includes('مع الوجبة') || text.includes('مع وجبة');
}

/** Meal times sorted by clock position, invalid entries dropped. */
function sortedMeals(mealTimes = {}) {
  return MEAL_KEYS.filter((key) => mealTimes[key])
    .map((key) => ({ key, label: MEAL_LABELS[key], seconds: toDaySeconds(mealTimes[key]) }))
    .filter((meal) => !Number.isNaN(meal.seconds))
    .sort((a, b) => a.seconds - b.seconds);
}

/** Seconds until the next meal, wrapping to tomorrow's first. `null` if none set. */
export function secondsUntilNextMeal(mealTimes, now = new Date()) {
  const meals = sortedMeals(mealTimes);
  if (meals.length === 0) return null;

  const current = secondsSinceMidnight(now);
  const upcoming = meals.find((meal) => meal.seconds > current);
  const target = upcoming ? upcoming.seconds : meals[0].seconds + DAY_SECONDS;
  return target - current;
}

/** "HH:MM:SS" until the next meal. */
export function mealCountdown(mealTimes, now = new Date()) {
  const remaining = secondsUntilNextMeal(mealTimes, now);
  if (remaining === null) return '--:--:--';
  return formatCountdown(remaining);
}

/** The next meal itself, or `null` when no meal times are configured. */
export function nextMeal(mealTimes, now = new Date()) {
  const meals = sortedMeals(mealTimes);
  if (meals.length === 0) return null;
  const current = secondsSinceMidnight(now);
  return meals.find((meal) => meal.seconds > current) || meals[0];
}

/** e.g. "8:00 AM — الإفطار". */
export function nextMealLabel(mealTimes, now = new Date()) {
  const meal = nextMeal(mealTimes, now);
  if (!meal) return '--:--';
  return `${formatSecondsAs12h(meal.seconds)} — ${meal.label}`;
}

/* ==========================================================================
   Course tracking
   ========================================================================== */

/**
 * Days left in a course, given a start date and a duration in days.
 * Returns `null` when the medicine has no defined duration (open-ended).
 */
export function courseDaysRemaining(med, now = new Date()) {
  const duration = Number(med?.durationDays ?? med?.duration);
  if (!Number.isFinite(duration) || duration <= 0) return null;

  const startRaw = med.startDate || med.createdAt;
  if (!startRaw) return duration;

  const start = new Date(startRaw);
  if (Number.isNaN(start.getTime())) return duration;

  start.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const elapsed = Math.floor((today - start) / 86400000);
  return Math.max(0, duration - elapsed);
}

/** True when a course has run its full length. */
export function isCourseComplete(med, now = new Date()) {
  const remaining = courseDaysRemaining(med, now);
  return remaining !== null && remaining <= 0;
}

/**
 * Key identifying one alarm firing, so the same dose is never announced twice.
 * Matches the legacy `firedKeys` format ("medId|HH:MM").
 */
export function doseAlarmKey(medId, doseSeconds) {
  const h = String(Math.floor(doseSeconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((doseSeconds % 3600) / 60)).padStart(2, '0');
  return `${medId}|${h}:${m}`;
}

/**
 * Doses that are due right now, within `toleranceSeconds` of their scheduled
 * time and not already fired. This is what drives the alarm.
 *
 * @returns {Array<{med: object, seconds: number, key: string}>}
 */
export function dueDoses(medicines = [], firedKeys = new Set(), now = new Date(), toleranceSeconds = 60) {
  const current = secondsSinceMidnight(now);
  const due = [];

  for (const med of medicines) {
    if (!med?.alarmEnabled) continue;
    if (isCourseComplete(med, now)) continue;

    for (const seconds of doseTimesInSeconds(med)) {
      if (Math.abs(current - seconds) > toleranceSeconds) continue;
      const key = doseAlarmKey(med.id, seconds);
      if (firedKeys.has(key)) continue;
      due.push({ med, seconds, key });
    }
  }

  return due;
}
